import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Pill, Syringe, Bandage, Wheat, Fence,
  Hammer, Timer, Scale, Caravan, MoreHorizontal, NotepadText,
  Tablets, PillBottle, Cylinder, BrushCleaning, Magnet, RulerDimensionLine, MessageSquare,
  CheckCircle, AlertTriangle,
  Plus, Pencil, Trash2, CalendarDays, BookOpen, Calendar, Download, FileText, Search,
} from 'lucide-react';
import ToothIcon from './ToothIcon';
import { toast } from 'sonner';
import api from '../../services/api';

import CareCatalogPanel from './CareCatalogPanel';
import CareLogTable from './CareLogTable';
import CareLogEntryDetailModal from './CareLogEntryDetailModal';
import CareLogEntryModal from './CareLogEntryModal';
import ScheduleCareModal from './ScheduleCareModal';
import LayupModal from './LayupModal';
import BulkEditScheduledModal from './BulkEditScheduledModal';
import EverydayRoutineSection from './EverydayRoutineSection';
import TurnoutLogModal from './TurnoutLogModal';
import TravelLogModal from './TravelLogModal';
import StatsLogModal from './StatsLogModal';
import QuickLogModal from './QuickLogModal';
import LinkedNoteDialog from './LinkedNoteDialog';
import GenerateCareInvoiceModal from './GenerateCareInvoiceModal';
import { useConfirm } from '../../components/common/ConfirmDialog';

// Quick Log — grouped by category to match the mockup layout
// Row 1: Medical (purple), Row 2: Daily Care (sky), Row 3: Maintenance (yellow) + Conditioning (emerald)
// Row 4: Stats/Travel/Other (no group bg), Row 5: Add Note (full-width)
const QL_MEDICAL     = [
  { key: 'meds',      label: 'Meds Given', icon: Tablets,  icon2: PillBottle, category: 'Medical',      type: 'Medication' },
  { key: 'procedure', label: 'Procedure',  icon: Syringe,                     category: 'Medical',      type: 'Procedure' },
  { key: 'layup',     label: 'Layup',      icon: Bandage,                     category: 'Medical',      type: 'Layup', isLayup: true },
];
const QL_DAILY       = [
  { key: 'fed',       label: 'Fed',        icon: Cylinder, icon2: Wheat,      category: 'Daily Care',   type: 'Feed' },
  { key: 'groomed',   label: 'Groomed',    icon: BrushCleaning,               category: 'Daily Care',   type: 'Groom' },
  { key: 'turnout',   label: 'Turned Out', icon: Fence,                       category: 'Daily Care',   type: 'Turnout' },
];
const QL_MAINTENANCE = [
  { key: 'farrier',   label: 'Farrier',    icon: Magnet,                      category: 'Maintenance',  type: 'Farrier' },
  { key: 'dental',    label: 'Dental',     icon: ToothIcon,                   category: 'Maintenance',  type: 'Dental' },
];
const QL_CONDITIONING = { key: 'worked', label: 'Worked', icon: Timer,  category: 'Conditioning', type: 'Exercise' };
const QL_MISC        = [
  { key: 'stats',     label: 'Stats Taken',icon: RulerDimensionLine,          category: 'Stats',        type: 'Vitals' },
  { key: 'travel',    label: 'Travel',     icon: Caravan,                     category: 'Travel',       type: 'Transport' },
  { key: 'other',     label: 'Other',      icon: MoreHorizontal,              category: 'Notes',        type: 'General Note' },
];

function formatDueDate(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  if (due < today) return (
    <><span className="text-red-600 font-semibold">Overdue</span>{` · ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}</>
  );
  if (due.toDateString() === today.toDateString()) return 'Due Today';
  return `Due ${due.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
}

function formatTime(t) {
  if (!t) return 'All Day';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

const fmtScheduledName = (item, catalog) => {
  const nameStr = item.name || '';
  const typeStr = item.type || '';
  const base = nameStr.toLowerCase().startsWith(typeStr.toLowerCase()) ? nameStr : typeStr ? `${typeStr} — ${nameStr}` : nameStr;

  // Prefer dosage/serving_size on the item itself; fall back to catalog lookup
  let extra = '';
  if (item.dosage) {
    extra = ` (${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''})`;
  } else if (item.serving_size != null) {
    extra = ` (${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''})`;
  } else if (catalog && item.care_catalog_item_id) {
    const cat = catalog.find(c => c.id === item.care_catalog_item_id);
    if (cat?.dosage) {
      extra = ` (${cat.dosage}${cat.dosage_unit ? ' ' + cat.dosage_unit : ''})`;
    } else if (cat?.serving_size != null) {
      extra = ` (${cat.serving_size}${cat.serving_size_units ? ' ' + cat.serving_size_units : ''})`;
    }
  }
  return base + extra;
};

const CARE_BORDER = {
  'Medical':      'border-l-4 border-l-purple-300',
  'Daily Care':   'border-l-4 border-l-sky-300',
  'Maintenance':  'border-l-4 border-l-yellow-300',
  'Conditioning': 'border-l-4 border-l-emerald-300',
  'Travel':       'border-l-4 border-l-indigo-300',
};

export default function FarmFolioTab({ horse }) {
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const isFarmMgr = currentUser?.roles?.includes('tenant_farm_manager');
  const horseId = horse?.id;

  const { data: tenant } = useQuery({
    queryKey: QK.tenant(currentUser?.tenant_id),
    queryFn: () => api.get(`/tenants/${currentUser.tenant_id}`).then(r => r.data),
    enabled: !!currentUser?.tenant_id,
  });
  const isFarm = tenant?.is_farm ?? null;

  const { data: farmData, isLoading: loading, refetch: refetchFarm } = useQuery({
    queryKey: QK.farmfolioAll(horseId),
    queryFn: async () => {
      const [todayRes, upcomingRes, scheduledRes, logRes, catalogRes, locationsRes, layupsRes] = await Promise.all([
        api.get(`/farmfolio/horse/${horseId}/due-today`),
        api.get(`/farmfolio/horse/${horseId}/upcoming?days=30`),
        api.get(`/farmfolio/scheduled-care?horse_id=${horseId}`),
        api.get(`/farmfolio/care-log?horse_id=${horseId}`),
        api.get('/farmfolio/care-catalog'),
        api.get('/farmfolio/locations'),
        api.get(`/farmfolio/layups?horse_id=${horseId}`),
      ]);
      const localToday = new Date().toLocaleDateString('sv');
      api.post(`/farmfolio/horse/${horseId}/routine/topup`).catch(() => {});
      return {
        dueToday: (todayRes.data || []).filter(i => i.next_due_date <= localToday),
        upcoming: (upcomingRes.data || []).filter(i => i.next_due_date > localToday),
        scheduled: scheduledRes.data || [],
        careLog: logRes.data || [],
        catalog: catalogRes.data || [],
        locations: locationsRes.data || [],
        layups: layupsRes.data || [],
      };
    },
    enabled: !!horseId && isFarm === true,
    onError: () => toast.error('Failed to load FarmFolio data'),
  });

  const dueToday  = farmData?.dueToday  ?? [];
  const upcoming  = farmData?.upcoming  ?? [];
  const scheduled = farmData?.scheduled ?? [];
  const careLog   = farmData?.careLog   ?? [];
  const catalog   = farmData?.catalog   ?? [];
  const locations = farmData?.locations ?? [];
  const layups    = farmData?.layups    ?? [];

  const fetchAll = useCallback(() => refetchFarm(), [refetchFarm]);

  useEffect(() => {
    if (horseId && isFarm) {
      api.post(`/farmfolio/horse/${horseId}/routine/auto-log`).catch(() => {});
    }
  }, [horseId, isFarm]);

  // Collapse state
  const [showCatalog, setShowCatalog] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  // Modal states
  const [quickLogModal, setQuickLogModal] = useState({ open: false, prefill: null, scheduledItem: null });
  const [noteLogModal, setNoteLogModal]   = useState({ open: false });
  const [turnoutModal, setTurnoutModal]   = useState({ open: false, item: null });
  const [travelModal, setTravelModal]     = useState({ open: false, item: null });
  const [statsModal, setStatsModal]       = useState({ open: false, item: null });
  const [scheduleModal, setScheduleModal] = useState({ open: false, editing: null });
  const [layupModal, setLayupModal] = useState({ open: false, editing: null, fromCareLog: false });
  const [editLogEntry, setEditLogEntry] = useState(null);
  const [careLogDetail, setCareLogDetail] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  // Manage Scheduled Care filters
  const [schedCategoryFilter, setSchedCategoryFilter] = useState('All');
  const [schedSearch, setSchedSearch]                 = useState('');
  const [schedBreedingFilter, setSchedBreedingFilter] = useState(false);
  const [selectedScheduledIds, setSelectedScheduledIds] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen]               = useState(false);

  const activeLayup = layups.find(l => l.is_active);

  const handleComplete = async (item) => {
    if (item.type === 'Turnout') {
      setTurnoutModal({ open: true, item });
    } else if (item.category === 'Travel') {
      setTravelModal({ open: true, item });
    } else if (item.category === 'Stats') {
      setStatsModal({ open: true, item });
    } else {
      setQuickLogModal({ open: true, prefill: null, scheduledItem: item });
    }
  };
  const handleDeleteScheduled = async (item) => {
    if (!await confirm({ title: `Remove "${item.name}" from schedule?`, destructive: true })) return;
    try {
      await api.delete(`/farmfolio/scheduled-care/${item.id}`);
      toast.success('Removed from schedule');
      fetchAll();
    } catch { toast.error('Failed to remove'); }
  };

  // ── Gate ─────────────────────────────────────────────────────────────────
  if (isFarm === null) return <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>;
  if (isFarm === false) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <div className="w-16 h-16 mx-auto bg-muted flex items-center justify-center rounded-full">
            <CalendarDays className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-lg">FarmFolio</h3>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            FarmFolio is a premium feature for farm tenants. Contact your system administrator to enable it.
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Care Catalog overlay ──────────────────────────────────────────────────
  if (showCatalog) {
    return (
      <div className="space-y-4">
        <CareCatalogPanel onClose={() => setShowCatalog(false)} onSaved={fetchAll} horse={horse} />
      </div>
    );
  }

  // ── Manage Scheduled Care overlay ─────────────────────────────────────────
  const handleExportScheduled = () => {
    const rows = [
      ['Category', 'Type', 'Name', 'Frequency', 'Next Due', 'Location', 'Routine'],
      ...scheduled.map(item => [
        item.category || '',
        item.type || '',
        item.name || '',
        (item.frequency_type || 'once').replace('_', ' '),
        item.next_due_date || '',
        item.location_name || '',
        item.is_routine ? 'Yes' : 'No',
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scheduled_care_${horse?.name?.replace(/\s+/g, '_') || 'horse'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (showSchedule) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" /> Manage Scheduled Care
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                  onClick={() => setScheduleModal({ open: true, editing: null })}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setLayupModal({ open: true, editing: null, fromCareLog: false })}>
                  + Layup
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowSchedule(false)}>Close</Button>
              </div>
            </div>
            {scheduled.length > 0 && (
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleExportScheduled}
                  data-testid="manage-scheduled-export-csv">
                  <Download className="w-3 h-3 mr-1" /> Export CSV
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            {scheduled.length === 0 ? (
              <p className="text-sm text-center py-8 text-muted-foreground italic">No scheduled care. Click "Add" to schedule care for this horse.</p>
            ) : (() => {
              const SCHED_CATS = ['All', 'Medical', 'Daily Care', 'Maintenance', 'Conditioning'];
              const SCHED_COLORS = {
                'All':          { active: 'bg-primary text-white border-primary',           inactive: 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50' },
                'Medical':      { active: 'bg-purple-100 text-purple-700 border-purple-300',     inactive: 'bg-purple-50 text-purple-500 border-purple-200 hover:bg-purple-100' },
                'Daily Care':   { active: 'bg-sky-100 text-sky-700 border-sky-300',              inactive: 'bg-sky-50 text-sky-500 border-sky-200 hover:bg-sky-100' },
                'Maintenance':  { active: 'bg-yellow-100 text-yellow-700 border-yellow-300',     inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100' },
                'Conditioning': { active: 'bg-emerald-100 text-emerald-700 border-emerald-300',  inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100' },
              };
              const localToday = new Date().toLocaleDateString('sv');
              const breedingCatalogIds = new Set((catalog || []).filter(c => c.is_breeding).map(c => c.id));
              const filteredScheduled = scheduled.filter(i => {
                const matchCat = schedCategoryFilter === 'All' || i.category === schedCategoryFilter;
                const matchQ   = !schedSearch || (i.name + ' ' + i.type).toLowerCase().includes(schedSearch.toLowerCase());
                const matchBreed = !schedBreedingFilter || (i.care_catalog_item_id ? breedingCatalogIds.has(i.care_catalog_item_id) : !!i.is_breeding);
                return matchCat && matchQ && matchBreed;
              });
              const todayItems    = filteredScheduled.filter(i => i.next_due_date && i.next_due_date <= localToday);
              const upcomingItems = filteredScheduled.filter(i => !i.next_due_date || i.next_due_date > localToday);

              const toggleSelSched = (id) => {
                const next = new Set(selectedScheduledIds);
                if (next.has(id)) next.delete(id); else next.add(id);
                setSelectedScheduledIds(next);
              };

              // Bulk edit eligibility: selected items must share same category+type+name
              const selItems = filteredScheduled.filter(i => selectedScheduledIds.has(i.id));
              const canBulkEdit = selItems.length > 1 &&
                selItems.every(i => i.category === selItems[0].category && i.type === selItems[0].type && i.name === selItems[0].name);

              const renderRow = (item) => (
                <div key={item.id}
                  className={`flex items-center gap-2 p-2.5 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
                  <input type="checkbox" checked={selectedScheduledIds.has(item.id)}
                    onChange={() => toggleSelSched(item.id)} className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {fmtScheduledName(item, catalog)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.next_due_date
                        ? formatDueDate(item.next_due_date)
                        : <span className="capitalize">{(item.frequency_type || 'once').replace('_', ' ')}</span>}
                      {item.preferred_time && <> @ {formatTime(item.preferred_time)}</>}
                      {item.location_name && <> · {item.location_name}</>}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => setScheduleModal({ open: true, editing: item })}
                      title="Edit"
                      data-testid={`scheduled-care-edit-${item.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => handleDeleteScheduled(item)}
                      title="Delete"
                      data-testid={`scheduled-care-delete-${item.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );

              return (
                <div className="pt-2 space-y-4">
                  {/* Category pills + text search */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <div className="flex flex-wrap gap-1">
                      {SCHED_CATS.map(c => (
                        <button key={c} type="button" onClick={() => setSchedCategoryFilter(c)}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                            schedCategoryFilter === c
                              ? (SCHED_COLORS[c]?.active  || SCHED_COLORS['All'].active)
                              : (SCHED_COLORS[c]?.inactive || SCHED_COLORS['All'].inactive)
                          }`}>{c}</button>
                      ))}
                      <button type="button" onClick={() => setSchedBreedingFilter(v => !v)}
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                          schedBreedingFilter
                            ? 'bg-pink-100 text-pink-700 border-pink-300'
                            : 'bg-pink-50 text-pink-400 border-pink-200 hover:bg-pink-100'
                        }`}>Breeding</button>
                    </div>
                    <div className="relative flex-1 min-w-[140px]">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input value={schedSearch} onChange={e => setSchedSearch(e.target.value)}
                        placeholder="Search…" className="h-7 text-xs pl-7" />
                    </div>
                  </div>

                  {/* Bulk actions toolbar */}
                  {selectedScheduledIds.size > 0 && (
                    <div className="flex items-center gap-2 p-2 bg-muted/20 border rounded text-xs">
                      <span className="font-medium">{selectedScheduledIds.size} selected</span>
                      {canBulkEdit && (
                        <Button size="sm" variant="outline" className="h-6 text-xs"
                          onClick={() => setBulkEditOpen(true)}>Bulk Edit</Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-6 text-xs ml-auto"
                        onClick={() => setSelectedScheduledIds(new Set())}>Clear</Button>
                    </div>
                  )}
                  {/* Today's Care */}
                  <div>
                    <p className="text-sm font-semibold mb-2">Today's Care</p>
                    {todayItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic py-2 text-center">No care due today.</p>
                    ) : (
                      <div className="space-y-2">{todayItems.map(renderRow)}</div>
                    )}
                  </div>

                  {/* Upcoming Care — all future items */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-semibold">Upcoming Care</p>
                      {upcomingItems.length > 0 && (
                        <Badge className="bg-primary/10 text-primary text-xs">{upcomingItems.length}</Badge>
                      )}
                    </div>
                    {upcomingItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic py-2 text-center">No upcoming care scheduled.</p>
                    ) : (
                      <div className="space-y-2">{upcomingItems.map(renderRow)}</div>
                    )}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        <ScheduleCareModal
          open={scheduleModal.open}
          onClose={() => setScheduleModal({ open: false, editing: null })}
          onSaved={fetchAll}
          horse={horse}
          locations={locations}
          catalogItems={catalog}
          editing={scheduleModal.editing}
        />

        <LayupModal
          open={layupModal.open}
          onClose={() => setLayupModal({ open: false, editing: null, fromCareLog: false })}
          onSaved={fetchAll}
          horse={horse}
          editing={layupModal.editing}
          catalogItems={catalog}
          fromCareLog={layupModal.fromCareLog}
        />

        <BulkEditScheduledModal
          open={bulkEditOpen}
          onClose={() => setBulkEditOpen(false)}
          onSaved={() => { setSelectedScheduledIds(new Set()); fetchAll(); setBulkEditOpen(false); }}
          items={scheduled.filter(i => selectedScheduledIds.has(i.id))}
          locations={locations}
          catalogItems={catalog}
        />
      </div>
    );
  }

  // ── Main layout ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* Active Layup Banner */}
      {/* Management buttons — top right, matching global view style */}
      <div className="flex justify-end flex-wrap gap-2">
        {!isFarmMgr && (
        <Button size="sm" variant="outline" className="h-8 text-xs"
          onClick={() => setInvoiceModalOpen(true)}
          data-testid="farmfolio-tab-generate-invoice-btn">
          <FileText className="w-3 h-3 mr-1" /> Generate Invoice
        </Button>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs"
          onClick={() => setShowSchedule(true)}>
          <Calendar className="w-3 h-3 mr-1" /> Manage Scheduled Care
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs"
          onClick={() => setShowCatalog(true)}>
          <BookOpen className="w-3 h-3 mr-1" /> Care Catalog
        </Button>
      </div>

      {activeLayup && (
        <div className="flex items-center gap-3 p-3 border border-red-200 bg-red-50 rounded">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">LAYUP — {activeLayup.reason}</p>
            <p className="text-xs text-red-600">
              Started {activeLayup.start_date}
              {activeLayup.duration_value
                ? ` · ${activeLayup.duration_value} ${activeLayup.duration_unit || 'days'}`
                : activeLayup.duration_days ? ` · ${activeLayup.duration_days} days` : ''}
              {' · '}Authorized by {activeLayup.authorized_by}
            </p>
            {Object.entries(activeLayup.restrictions || {}).filter(([, v]) => v).map(([k]) => (
              <Badge key={k} className="mr-1 mt-1 text-xs bg-red-100 text-red-700">
                {k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </Badge>
            ))}
          </div>
          <Button size="sm" variant="outline" className="shrink-0 text-xs border-red-300"
            onClick={() => setLayupModal({ open: true, editing: activeLayup, fromCareLog: false })}>Edit</Button>
        </div>
      )}

      {/* Management buttons moved to top, above layup banner */}

      {/* ── Two-column section ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-stretch">

        {/* LEFT — 2/3: Care card fixed 400px + spacer + buttons pinned to bottom */}
        <div className="lg:col-span-2 flex flex-col gap-3 lg:h-full">

          <Card>
            <CardContent
              className="pt-4 space-y-4"
              style={{ height: '460px', overflowY: 'auto' }}
            >

              {/* ── Today's Care ───────────────────────────────────── */}
              <div>
                <p className="text-sm font-semibold mb-2">Today's Care</p>
                {loading ? (
                  <p className="text-sm text-center py-4 text-muted-foreground">Loading…</p>
                ) : dueToday.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-2 text-center">
                    No care due today. Use Quick Log to record care, or Manage Scheduled Care to add items.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {dueToday.map(item => (
                      <div key={item.id} className={`flex items-center justify-between p-3 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
                        <div>
                          <p className="text-sm font-medium">{fmtScheduledName(item, catalog)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDueDate(item.next_due_date)} @ {formatTime(item.preferred_time)}
                            {item.location_name && <> · {item.location_name}</>}
                            {item.dosage && <> · {item.dosage} {item.dosage_unit || ''}</>}
                          </p>
                        </div>
                        <Button size="sm" className="h-7 text-xs border border-primary text-primary hover:opacity-90 shrink-0"
                          style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}
                          onClick={() => handleComplete(item)}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Complete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Care in Next 30 Days ───────────────────────────── */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm font-semibold">Care in Next 30 Days</p>
                  {upcoming.length > 0 && (
                    <Badge className="bg-primary/10 text-primary text-xs">{upcoming.length}</Badge>
                  )}
                </div>
                {loading ? null : upcoming.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-2 text-center">No upcoming care scheduled.</p>
                ) : (
                  <div className="space-y-2">
                    {upcoming.map(item => (
                      <div key={item.id} className={`flex items-center justify-between p-2.5 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
                        <div>
                          <p className="text-sm font-medium">{fmtScheduledName(item, catalog)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDueDate(item.next_due_date)} @ {formatTime(item.preferred_time)}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                          onClick={() => handleComplete(item)}>Complete</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </CardContent>
          </Card>

          {/* Management buttons moved to top right header area above */}
        </div>

        {/* RIGHT — 1/3: Everyday Routine + Quick Log */}
        <div className="flex flex-col gap-3">

          {/* Everyday Routine — collapsed by default */}
          <EverydayRoutineSection
            scheduledItems={scheduled}
            horse={horse}
            locations={locations}
            catalogItems={catalog}
            onSaved={fetchAll}
          />

          {/* Quick Log */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Quick Log</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {(() => {
                const qlBtn = (item) => {
                  const Icon  = item.icon;
                  const Icon2 = item.icon2;
                  return (
                    <button
                      key={item.key}
                      onClick={() => item.isLayup
                        ? setLayupModal({ open: true, editing: null, fromCareLog: false })
                        : item.key === 'turnout'
                          ? setTurnoutModal({ open: true, item: null })
                          : setQuickLogModal({ open: true, prefill: { category: item.category, type: item.type } })
                      }
                      className="flex flex-col items-center justify-center gap-1 p-2 bg-white rounded-xl hover:shadow-md transition-shadow"
                    >
                      {Icon2 ? (
                        <div className="flex items-center gap-0.5">
                          <Icon  className="w-5 h-5 text-primary" />
                          <Icon2 className="w-5 h-5 text-primary" />
                        </div>
                      ) : (
                        <Icon className="w-5 h-5 text-primary" />
                      )}
                      <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                    </button>
                  );
                };
                return (
                  <div className="space-y-2">
                    {/* Row 1 — Medical (lavender) */}
                    <div className="bg-purple-100 rounded-2xl p-1.5" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                      {QL_MEDICAL.map(qlBtn)}
                    </div>
                    {/* Row 2 — Daily Care (sky blue) */}
                    <div className="bg-sky-100 rounded-2xl p-1.5" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                      {QL_DAILY.map(qlBtn)}
                    </div>
                    {/* Row 3 — Maintenance (yellow, 2 cols) + Conditioning (mint, 1 col) */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                      <div className="col-span-2 bg-yellow-100 rounded-2xl p-1.5" style={{gridColumn:'span 2',display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'6px'}}>
                        {QL_MAINTENANCE.map(qlBtn)}
                      </div>
                      <div className="bg-emerald-100 rounded-2xl p-1.5" style={{display:'grid',gridTemplateColumns:'1fr'}}>
                        {qlBtn(QL_CONDITIONING)}
                      </div>
                    </div>
                    {/* Row 4 — Stats / Travel / Other */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                      {QL_MISC.map(item => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.key}
                            onClick={() => item.key === 'travel'
                              ? setTravelModal({ open: true, item: null })
                              : item.key === 'stats'
                                ? setStatsModal({ open: true, item: null })
                                : item.key === 'other'
                                  ? setQuickLogModal({ open: true, prefill: null })
                                  : setQuickLogModal({ open: true, prefill: { category: item.category, type: item.type } })
                            }
                            className="flex flex-col items-center gap-1 p-2 bg-white border rounded-xl hover:shadow-md transition-shadow"
                          >
                            <Icon className="w-5 h-5 text-primary" />
                            <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {/* Row 5 — Add Note (full width) */}
                    <button
                      onClick={() => setNoteLogModal({ open: true })}
                      className="w-full border rounded-xl p-2.5 flex items-center justify-center gap-2 text-primary hover:bg-muted/30 transition-colors"
                    >
                      <NotepadText className="w-5 h-5" />
                      <span className="text-sm font-semibold">Add Note</span>
                    </button>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Full-width Care Log ───────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Care Log</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <CareLogTable
            entries={careLog}
            loading={loading}
            catalogItems={catalog}
            onRowClick={async (entry) => {
              if (entry.is_layup && entry.layup_id) {
                try {
                  const res = await api.get(`/farmfolio/layups?horse_id=${horse.id}`);
                  const layup = (res.data || []).find(l => l.id === entry.layup_id);
                  if (layup) {
                    setLayupModal({ open: true, editing: layup, fromCareLog: true });
                    return;
                  }
                } catch { /* fall through to detail modal */ }
              }
              setCareLogDetail(entry);
            }}
            onDeleted={() => fetchAll()}
          />
        </CardContent>
      </Card>

      {/* ── Modals ────────────────────────────────────────────────────────────── */}
      <StatsLogModal
        open={statsModal.open}
        onClose={() => setStatsModal({ open: false, item: null })}
        onSaved={() => { setStatsModal({ open: false, item: null }); fetchAll(); }}
        horse={horse}
        locations={locations}
        scheduledItem={statsModal.item}
      />

      <TravelLogModal
        open={travelModal.open}
        onClose={() => setTravelModal({ open: false, item: null })}
        onSaved={() => fetchAll()}
        horse={horse}
        locations={locations}
        scheduledItem={travelModal.item}
      />

      <TurnoutLogModal
        open={turnoutModal.open}
        onClose={() => setTurnoutModal({ open: false, item: null })}
        onSaved={() => fetchAll()}
        horse={horse}
        locations={locations}
        scheduledItem={turnoutModal.item}
        activeLayup={activeLayup}
      />

      {/* QuickLogModal — all quick log buttons except Layup/Turnout/Travel/Stats/Notes */}
      <QuickLogModal
        open={quickLogModal.open}
        onClose={() => setQuickLogModal({ open: false, prefill: null, scheduledItem: null })}
        onSaved={(entry) => {
          // Immediately add entry to careLog if we got one back, then refresh all
          if (entry && entry.id) fetchAll();
          fetchAll();
        }}
        horse={horse}
        locations={locations}
        catalogItems={catalog}
        prefill={quickLogModal.prefill}
        scheduledItem={quickLogModal.scheduledItem}
        activeLayup={activeLayup}
      />

      <CareLogEntryDetailModal
        open={!!careLogDetail}
        onClose={() => setCareLogDetail(null)}
        entry={careLogDetail}
        locations={locations}
        onDeleted={(id) => { fetchAll(); setCareLogDetail(null); }}
        onUpdated={(updated) => { fetchAll(); setCareLogDetail(updated); }}
      />

      {/* LinkedNoteDialog — Add Note button, passes care log for entry picker */}
      <LinkedNoteDialog
        open={noteLogModal.open}
        onClose={() => setNoteLogModal({ open: false })}
        onSaved={() => setNoteLogModal({ open: false })}
        horse={horse}
        recentCareLog={careLog.slice(0, 30)}
      />

      {/* CareLogEntryModal — edit existing entries only */}
      <CareLogEntryModal
        open={!!editLogEntry}
        onClose={() => setEditLogEntry(null)}
        onSaved={(entry) => {
          fetchAll();
        }}
        horse={horse}
        locations={locations}
        catalogItems={catalog}
        prefill={editLogEntry}
      />

      <ScheduleCareModal
        open={scheduleModal.open}
        onClose={() => setScheduleModal({ open: false, editing: null })}
        onSaved={fetchAll}
        horse={horse}
        locations={locations}
        catalogItems={catalog}
        editing={scheduleModal.editing}
      />

      <LayupModal
        open={layupModal.open}
        onClose={() => setLayupModal({ open: false, editing: null, fromCareLog: false })}
        onSaved={fetchAll}
        horse={horse}
        editing={layupModal.editing}
        catalogItems={catalog}
        fromCareLog={layupModal.fromCareLog}
      />

      <GenerateCareInvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        horse={horse}
      />
    </div>
  );
}
